<template>
  <div class="price-groups-page">

    <!-- ====== DESKTOP LAYOUT ≥992px ====== -->
    <template v-if="!mobileViewport">
      <div class="page-toolbar">
        <div>
          <p class="page-eyebrow">Inventario / Precios</p>
          <h2>Grupos de precio</h2>
        </div>
        <div class="toolbar-actions">
          <select v-model="statusFilter" class="form-select form-select-sm">
            <option value="todos">Estado: Todos</option>
            <option value="activos">Estado: Activos</option>
            <option value="inactivos">Estado: Inactivos</option>
          </select>
          <select v-model="unitFilter" class="form-select form-select-sm">
            <option value="0">Unidad: Todas</option>
            <option v-for="unit in units" :key="`f-${unit.unidad_medida_id}`" :value="String(unit.unidad_medida_id)">
              {{ unit.nombre }}
            </option>
          </select>
          <label class="form-check form-switch mb-0">
            <input v-model="includeInactive" class="form-check-input" type="checkbox" @change="fetchGroups" />
            <span class="form-check-label">Incluir inactivos</span>
          </label>
          <button class="btn btn-outline-secondary" type="button" @click="fetchGroups">
            <i class="bi bi-arrow-clockwise"></i>
          </button>
        </div>
      </div>

      <div v-if="message" :class="['alert', messageType === 'error' ? 'alert-danger' : 'alert-success']">{{ message }}</div>

      <div class="layout-grid">
        <section class="card panel">
          <div class="card-body">
            <h5 class="mb-3">Crear / editar grupo</h5>
            <form class="row g-3" @submit.prevent="saveGroup">
              <div class="col-12">
                <label class="form-label">Nombre</label>
                <input v-model.trim="groupForm.nombre" class="form-control" type="text" maxlength="120" required />
              </div>
              <div class="col-12">
                <label class="form-label">Unidad operativa</label>
                <select v-model.number="groupForm.unidad_medida_id" class="form-select" required>
                  <option :value="null" disabled>Seleccionar unidad</option>
                  <option v-for="unit in units" :key="unit.unidad_medida_id" :value="Number(unit.unidad_medida_id)">
                    {{ unit.nombre }}
                  </option>
                </select>
              </div>
              <div class="col-12">
                <label class="form-label">Descripción</label>
                <textarea v-model.trim="groupForm.descripcion" class="form-control" rows="2"></textarea>
              </div>
              <div class="col-12 d-flex gap-2">
                <button class="btn btn-dark" :disabled="loadingSave" type="submit">
                  {{ groupForm.grupo_precio_id ? 'Guardar cambios' : 'Crear grupo' }}
                </button>
                <button v-if="groupForm.grupo_precio_id" class="btn btn-outline-secondary" type="button" @click="resetGroupForm">
                  Cancelar edición
                </button>
              </div>
            </form>

            <hr class="my-4" />

            <h6 class="mb-3">Listado de grupos</h6>
            <div v-if="loadingGroups" class="text-muted">Cargando grupos...</div>
            <div v-else-if="groups.length === 0" class="text-muted">Sin grupos cargados.</div>
            <div v-else-if="filteredGroups.length === 0" class="text-muted">No hay grupos para los filtros seleccionados.</div>
            <div v-else class="group-list">
              <button
                v-for="group in filteredGroups"
                :key="group.grupo_precio_id"
                type="button"
                :class="['group-item', selectedGroupId === group.grupo_precio_id ? 'active' : '']"
                @click="selectGroup(group.grupo_precio_id)"
              >
                <div>
                  <strong>{{ group.nombre }}</strong>
                  <div class="small text-muted">{{ group.unidad_medida_nombre }} · {{ group.total_productos_activos }} productos</div>
                </div>
                <span :class="['state-pill', group.activo ? 'on' : 'off']">{{ group.activo ? 'Activo' : 'Inactivo' }}</span>
              </button>
            </div>
          </div>
        </section>

        <section class="card panel" v-if="selectedGroup">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start gap-3 mb-3">
              <div>
                <h5 class="mb-1">{{ selectedGroup.nombre }}</h5>
                <div class="text-muted small">Unidad: {{ selectedGroup.unidad_medida_nombre }}</div>
              </div>
              <div class="d-flex gap-2">
                <button class="btn btn-outline-secondary btn-sm" type="button" @click="editCurrentGroup">Editar</button>
                <button
                  :class="['btn btn-sm', selectedGroup.activo ? 'btn-outline-danger' : 'btn-outline-success']"
                  type="button"
                  @click="toggleGroupStatus(selectedGroup)"
                >
                  {{ selectedGroup.activo ? 'Inactivar' : 'Activar' }}
                </button>
              </div>
            </div>

            <p class="text-muted mb-3">{{ selectedGroup.descripcion || 'Sin descripción' }}</p>

            <div class="card mb-3">
              <div class="card-body">
                <h6 class="mb-3">Agregar producto</h6>
                <div class="row g-2 align-items-end">
                  <div class="col-md-4">
                    <label class="form-label">Por código de barras</label>
                    <input v-model.trim="barcodeInput" class="form-control" type="text" placeholder="Escanear o escribir" @keyup.enter="addByBarcode" />
                  </div>
                  <div class="col-md-2">
                    <button class="btn btn-outline-dark w-100" type="button" @click="addByBarcode">Agregar</button>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Por búsqueda escrita</label>
                    <input v-model.trim="searchInput" class="form-control" type="search" placeholder="Nombre, SKU o barcode" @input="searchProducts" />
                    <div v-if="searchResults.length" class="search-results mt-1">
                      <button
                        v-for="product in searchResults"
                        :key="product.producto_id"
                        class="search-item"
                        type="button"
                        @click="addByProductId(product.producto_id)"
                      >
                        <strong>{{ product.nombre }}</strong>
                        <small>{{ product.codigo_producto || product.codigo_barras || 'Sin código' }} · {{ product.unidad_medida_nombre }}</small>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="card mb-3">
              <div class="card-body">
                <h6 class="mb-3">Productos del grupo</h6>
                <div v-if="!selectedGroup.productos?.length" class="text-muted">Sin productos activos en el grupo.</div>
                <div v-else class="table-responsive">
                  <table class="table table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th>Código</th>
                        <th>Unidad</th>
                        <th class="text-end">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="product in selectedGroup.productos" :key="product.producto_id">
                        <td>{{ product.nombre }}</td>
                        <td>{{ product.codigo_producto || product.codigo_barras || 'Sin código' }}</td>
                        <td>{{ product.unidad_medida_nombre }}</td>
                        <td class="text-end">
                          <button class="btn btn-outline-danger btn-sm" type="button" @click="removeProduct(product.producto_id)">Quitar</button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div class="card">
              <div class="card-body">
                <h6 class="mb-3">Aplicar precio masivo</h6>
                <form class="row g-2" @submit.prevent="applyPrice">
                  <div class="col-md-3">
                    <label class="form-label">Precio venta</label>
                    <MoneyInput v-model="priceForm.precio_venta" placeholder="0" />
                  </div>
                  <div class="col-md-2">
                    <label class="form-label">Desde</label>
                    <input v-model.number="priceForm.cantidad_desde" class="form-control" type="number" min="1" required />
                  </div>
                  <div class="col-md-2">
                    <label class="form-label">Hasta</label>
                    <input v-model.number="priceForm.cantidad_hasta" class="form-control" type="number" min="1" />
                  </div>
                  <div class="col-md-3">
                    <label class="form-label">Vigencia desde</label>
                    <input v-model="priceForm.vigencia_desde" class="form-control" type="date" />
                  </div>
                  <div class="col-md-2 d-grid">
                    <button class="btn btn-dark mt-md-4" type="submit">Aplicar</button>
                  </div>
                </form>
                <div v-if="applyResult" class="alert alert-light border mt-3 mb-0">
                  <div class="fw-semibold mb-2">Resultado de aplicación</div>
                  <div class="small mb-1">
                    Actualizados: <strong>{{ applyResult.resumen?.actualizados || 0 }}</strong> ·
                    Omitidos: <strong>{{ applyResult.resumen?.omitidos || 0 }}</strong>
                  </div>
                  <div v-if="(applyResult.omitidos || []).length" class="small text-danger">
                    <div class="fw-semibold">Productos omitidos:</div>
                    <ul class="mb-0 ps-3">
                      <li v-for="item in applyResult.omitidos" :key="`omit-${item.producto_id}`">
                        {{ item.producto_id }} - {{ item.reason || item.code }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section v-else class="card panel">
          <div class="card-body text-muted">Seleccioná un grupo para gestionarlo.</div>
        </section>
      </div>
    </template>

    <!-- ====== MOBILE LAYOUT ≤991.98px ====== -->
    <template v-else>

      <!-- ── Step: listado ── -->
      <div v-if="mobileStep === 'listado'">
        <div class="mobile-header">
          <div>
            <p class="page-eyebrow">Inventario / Precios</p>
            <h5 class="mb-0">Grupos de precio</h5>
          </div>
          <div class="d-flex gap-2">
            <button class="btn btn-outline-secondary btn-sm" type="button" :class="{ active: showMobileFilters }" @click="toggleMobileFilters">
              <i class="bi bi-funnel"></i>
            </button>
            <button class="btn btn-outline-secondary btn-sm" type="button" @click="fetchGroups" :disabled="loadingGroups">
              <i class="bi bi-arrow-clockwise"></i>
            </button>
            <button class="btn btn-dark btn-sm" type="button" @click="mobileGoToFormulario">
              <i class="bi bi-plus-lg me-1"></i>Nuevo
            </button>
          </div>
        </div>

        <div v-if="showMobileFilters" class="card mb-3">
          <div class="card-body py-2">
            <div class="d-flex flex-column gap-2">
              <select v-model="statusFilter" class="form-select form-select-sm">
                <option value="todos">Estado: Todos</option>
                <option value="activos">Solo activos</option>
                <option value="inactivos">Solo inactivos</option>
              </select>
              <select v-model="unitFilter" class="form-select form-select-sm">
                <option value="0">Unidad: Todas</option>
                <option v-for="unit in units" :key="`fm-${unit.unidad_medida_id}`" :value="String(unit.unidad_medida_id)">
                  {{ unit.nombre }}
                </option>
              </select>
              <label class="form-check form-switch mb-0">
                <input v-model="includeInactive" class="form-check-input" type="checkbox" @change="fetchGroups" />
                <span class="form-check-label small">Incluir inactivos</span>
              </label>
            </div>
          </div>
        </div>

        <div v-if="message" :class="['alert mb-3', messageType === 'error' ? 'alert-danger' : 'alert-success']">{{ message }}</div>

        <div v-if="loadingGroups" class="text-center py-5 text-muted">
          <div class="spinner-border spinner-border-sm me-2" aria-hidden="true"></div>Cargando grupos...
        </div>
        <div v-else-if="filteredGroups.length === 0" class="text-center py-5 text-muted">Sin grupos para los filtros actuales.</div>
        <div v-else class="mobile-group-cards">
          <button
            v-for="group in filteredGroups"
            :key="group.grupo_precio_id"
            class="mobile-group-card"
            type="button"
            @click="mobileGoToDetalle(group.grupo_precio_id)"
          >
            <div class="mobile-group-card-info">
              <strong class="d-block mb-1">{{ group.nombre }}</strong>
              <small class="text-muted">{{ group.unidad_medida_nombre }} · {{ group.total_productos_activos }} productos</small>
            </div>
            <div class="d-flex align-items-center gap-2 flex-shrink-0">
              <span :class="['state-pill', group.activo ? 'on' : 'off']">{{ group.activo ? 'Activo' : 'Inactivo' }}</span>
              <i class="bi bi-chevron-right text-muted"></i>
            </div>
          </button>
        </div>
      </div>

      <!-- ── Step: formulario ── -->
      <div v-else-if="mobileStep === 'formulario'">
        <div class="mobile-header">
          <button class="btn btn-link p-0 text-body text-decoration-none" type="button" @click="mobileGoBack">
            <i class="bi bi-arrow-left me-1"></i>{{ mobileFormOrigin === 'detalle' ? 'Detalle' : 'Grupos' }}
          </button>
          <span class="fw-semibold">{{ groupForm.grupo_precio_id ? 'Editar grupo' : 'Nuevo grupo' }}</span>
        </div>

        <div v-if="message" :class="['alert mb-3', messageType === 'error' ? 'alert-danger' : 'alert-success']">{{ message }}</div>

        <div class="card">
          <div class="card-body">
            <form @submit.prevent="saveGroup">
              <div class="mb-3">
                <label class="form-label fw-semibold">Nombre <span class="text-danger">*</span></label>
                <input v-model.trim="groupForm.nombre" class="form-control" type="text" maxlength="120" required autofocus />
              </div>
              <div class="mb-3">
                <label class="form-label fw-semibold">Unidad operativa <span class="text-danger">*</span></label>
                <select v-model.number="groupForm.unidad_medida_id" class="form-select" required :disabled="!!groupForm.grupo_precio_id">
                  <option :value="null" disabled>Seleccionar unidad</option>
                  <option v-for="unit in units" :key="unit.unidad_medida_id" :value="Number(unit.unidad_medida_id)">
                    {{ unit.nombre }}
                  </option>
                </select>
                <div v-if="groupForm.grupo_precio_id" class="form-text">La unidad no se puede cambiar una vez creado el grupo.</div>
              </div>
              <div class="mb-4">
                <label class="form-label">Descripción</label>
                <textarea v-model.trim="groupForm.descripcion" class="form-control" rows="3"></textarea>
              </div>
              <div class="mobile-form-footer">
                <button class="btn btn-outline-secondary" type="button" @click="mobileGoBack">Cancelar</button>
                <button class="btn btn-dark" :disabled="loadingSave" type="submit">
                  <span v-if="loadingSave" class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
                  {{ groupForm.grupo_precio_id ? 'Guardar cambios' : 'Crear grupo' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- ── Step: detalle ── -->
      <div v-else-if="mobileStep === 'detalle' && selectedGroup">
        <div class="mobile-header">
          <button class="btn btn-link p-0 text-body text-decoration-none" type="button" @click="mobileGoBack">
            <i class="bi bi-arrow-left me-1"></i>Grupos
          </button>
          <div class="d-flex gap-2">
            <button class="btn btn-outline-secondary btn-sm" type="button" @click="mobileEditCurrentGroup">Editar</button>
            <button
              :class="['btn btn-sm', selectedGroup.activo ? 'btn-outline-danger' : 'btn-outline-success']"
              type="button"
              @click="toggleGroupStatus(selectedGroup)"
            >
              {{ selectedGroup.activo ? 'Inactivar' : 'Activar' }}
            </button>
          </div>
        </div>

        <div class="mobile-group-info mb-3">
          <h5 class="mb-1">{{ selectedGroup.nombre }}</h5>
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <span :class="['state-pill', selectedGroup.activo ? 'on' : 'off']">{{ selectedGroup.activo ? 'Activo' : 'Inactivo' }}</span>
            <span class="text-muted small">{{ selectedGroup.unidad_medida_nombre }}</span>
          </div>
          <p v-if="selectedGroup.descripcion" class="text-muted small mt-2 mb-0">{{ selectedGroup.descripcion }}</p>
        </div>

        <div v-if="message" :class="['alert mb-3', messageType === 'error' ? 'alert-danger' : 'alert-success']">{{ message }}</div>

        <!-- Agregar producto con control segmentado -->
        <div class="card mb-3">
          <div class="card-body">
            <h6 class="mb-3">Agregar producto</h6>
            <div class="mobile-add-tabs mb-3">
              <button :class="['mobile-tab', mobileAddTab === 'codigo' ? 'active' : '']" type="button" @click="mobileAddTab = 'codigo'">
                <i class="bi bi-upc-scan me-1"></i>Código
              </button>
              <button :class="['mobile-tab', mobileAddTab === 'buscar' ? 'active' : '']" type="button" @click="mobileAddTab = 'buscar'">
                <i class="bi bi-search me-1"></i>Buscar
              </button>
            </div>
            <div v-if="mobileAddTab === 'codigo'" class="d-flex gap-2">
              <input v-model.trim="barcodeInput" class="form-control" type="text" inputmode="numeric" placeholder="Código de barras" @keyup.enter="addByBarcode" />
              <button class="btn btn-outline-dark flex-shrink-0" type="button" @click="addByBarcode">Agregar</button>
            </div>
            <div v-else>
              <input v-model.trim="searchInput" class="form-control" type="search" placeholder="Nombre, SKU o barcode" @input="searchProducts" />
              <div v-if="searchResults.length" class="search-results mt-2">
                <button
                  v-for="product in searchResults"
                  :key="product.producto_id"
                  class="search-item"
                  type="button"
                  @click="addByProductId(product.producto_id)"
                >
                  <strong>{{ product.nombre }}</strong>
                  <small>{{ product.codigo_producto || product.codigo_barras || 'Sin código' }} · {{ product.unidad_medida_nombre }}</small>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Productos del grupo como cards -->
        <div class="card mb-3">
          <div class="card-body">
            <h6 class="mb-3">
              Productos del grupo
              <span class="text-muted fw-normal">({{ selectedGroup.productos?.length || 0 }})</span>
            </h6>
            <div v-if="!selectedGroup.productos?.length" class="text-muted small">Sin productos en el grupo.</div>
            <div v-else class="product-card-list">
              <div v-for="product in selectedGroup.productos" :key="product.producto_id" class="product-card-item">
                <div class="product-card-info">
                  <strong class="d-block">{{ product.nombre }}</strong>
                  <small class="text-muted">{{ product.codigo_producto || product.codigo_barras || 'Sin código' }} · {{ product.unidad_medida_nombre }}</small>
                </div>
                <button class="btn btn-outline-danger btn-sm flex-shrink-0" type="button" @click="removeProduct(product.producto_id)">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Aplicar precio masivo — layout vertical -->
        <div class="card mb-4">
          <div class="card-body">
            <h6 class="mb-3">Aplicar precio masivo</h6>
            <form @submit.prevent="applyPrice">
              <div class="mb-3">
                <label class="form-label fw-semibold">Precio venta <span class="text-danger">*</span></label>
                <MoneyInput v-model="priceForm.precio_venta" class="form-control-lg" placeholder="0" />
              </div>
              <div class="row g-3 mb-3">
                <div class="col-6">
                  <label class="form-label">Cantidad desde <span class="text-danger">*</span></label>
                  <input v-model.number="priceForm.cantidad_desde" class="form-control" type="number" min="1" required />
                </div>
                <div class="col-6">
                  <label class="form-label">Hasta</label>
                  <input v-model.number="priceForm.cantidad_hasta" class="form-control" type="number" min="1" placeholder="Sin límite" />
                </div>
              </div>
              <div class="mb-4">
                <label class="form-label">Vigencia desde</label>
                <input v-model="priceForm.vigencia_desde" class="form-control" type="date" />
              </div>
              <button class="btn btn-dark w-100 py-2" type="submit">
                <i class="bi bi-check2-circle me-1"></i>Aplicar precio al grupo
              </button>
            </form>
            <div v-if="applyResult" class="alert alert-light border mt-3 mb-0">
              <div class="fw-semibold mb-1">Resultado</div>
              <div class="small">
                Actualizados: <strong>{{ applyResult.resumen?.actualizados || 0 }}</strong> ·
                Omitidos: <strong>{{ applyResult.resumen?.omitidos || 0 }}</strong>
              </div>
              <div v-if="(applyResult.omitidos || []).length" class="small text-danger mt-1">
                <div class="fw-semibold">Omitidos:</div>
                <ul class="mb-0 ps-3">
                  <li v-for="item in applyResult.omitidos" :key="`omit-${item.producto_id}`">
                    {{ item.producto_id }} - {{ item.reason || item.code }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import MoneyInput from '@/components/MoneyInput.vue';
import api from '@/services/apiService';
import {
  listPriceGroups,
  getPriceGroup,
  createPriceGroup,
  updatePriceGroup,
  activatePriceGroup,
  deactivatePriceGroup,
  searchPriceGroupProducts,
  addProductToPriceGroup,
  removeProductFromPriceGroup,
  applyPriceToGroup
} from '@/services/priceGroupsService';

export default {
  name: 'GruposPrecioView',
  components: { MoneyInput },
  data() {
    return {
      includeInactive: false,
      loadingGroups: false,
      loadingSave: false,
      groups: [],
      selectedGroupId: null,
      selectedGroup: null,
      units: [],
      searchInput: '',
      searchResults: [],
      barcodeInput: '',
      statusFilter: 'todos',
      unitFilter: '0',
      applyResult: null,
      message: '',
      messageType: 'ok',
      groupForm: {
        grupo_precio_id: null,
        nombre: '',
        descripcion: '',
        unidad_medida_id: null
      },
      priceForm: {
        precio_venta: null,
        cantidad_desde: 1,
        cantidad_hasta: null,
        vigencia_desde: ''
      },
      mobileViewport: false,
      mobileStep: 'listado',
      mobileFormOrigin: 'listado',
      mobileAddTab: 'codigo',
      showMobileFilters: false
    };
  },
  computed: {
    filteredGroups() {
      return this.groups.filter((group) => {
        const statusOk = this.statusFilter === 'todos'
          ? true
          : this.statusFilter === 'activos'
            ? group.activo
            : !group.activo;
        const unitOk = this.unitFilter === '0'
          ? true
          : Number(group.unidad_medida_id) === Number(this.unitFilter);
        return statusOk && unitOk;
      });
    }
  },
  watch: {
    statusFilter() {
      this.syncSelectedGroupWithFilters();
    },
    unitFilter() {
      this.syncSelectedGroupWithFilters();
    }
  },
  async mounted() {
    this.syncMobileViewport();
    window.addEventListener('resize', this.syncMobileViewport);
    await Promise.all([this.fetchUnits(), this.fetchGroups()]);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.syncMobileViewport);
  },
  methods: {
    notify(text, type = 'ok') {
      this.message = text;
      this.messageType = type;
      setTimeout(() => {
        if (this.message === text) this.message = '';
      }, 3500);
    },
    readError(error, fallback) {
      return error?.userMessage || error?.response?.data?.message || fallback;
    },
    syncMobileViewport() {
      this.mobileViewport = window.innerWidth < 992;
    },
    toggleMobileFilters() {
      this.showMobileFilters = !this.showMobileFilters;
    },
    async mobileGoToDetalle(groupId) {
      await this.selectGroup(groupId);
      this.mobileStep = 'detalle';
    },
    mobileGoToFormulario() {
      this.resetGroupForm();
      this.mobileFormOrigin = 'listado';
      this.mobileStep = 'formulario';
    },
    mobileEditCurrentGroup() {
      this.editCurrentGroup();
      this.mobileFormOrigin = 'detalle';
      this.mobileStep = 'formulario';
    },
    mobileGoBack() {
      const origin = this.mobileStep === 'formulario' ? this.mobileFormOrigin : null;
      this.resetGroupForm();
      this.mobileAddTab = 'codigo';
      this.mobileStep = origin === 'detalle' ? 'detalle' : 'listado';
    },
    async fetchUnits() {
      try {
        const { data } = await api.get('/api/measure-units', { page: 1, limit: 200 });
        const rows = Array.isArray(data?.data?.data) ? data.data.data : Array.isArray(data?.data) ? data.data : [];
        this.units = rows;
      } catch (error) {
        this.notify(this.readError(error, 'No se pudieron cargar unidades de medida'), 'error');
      }
    },
    async fetchGroups() {
      this.loadingGroups = true;
      try {
        this.groups = await listPriceGroups({ includeInactive: this.includeInactive });
        if (!this.filteredGroups.length) {
          this.selectedGroup = null;
          this.selectedGroupId = null;
          return;
        }
        if (!this.selectedGroupId || !this.filteredGroups.find((g) => g.grupo_precio_id === this.selectedGroupId)) {
          this.selectedGroupId = this.filteredGroups[0].grupo_precio_id;
        }
        await this.selectGroup(this.selectedGroupId);
      } catch (error) {
        this.notify(this.readError(error, 'No se pudieron cargar los grupos'), 'error');
      } finally {
        this.loadingGroups = false;
      }
    },
    async selectGroup(groupId) {
      this.selectedGroupId = Number(groupId);
      try {
        this.selectedGroup = await getPriceGroup(this.selectedGroupId);
      } catch (error) {
        this.notify(this.readError(error, 'No se pudo cargar el grupo seleccionado'), 'error');
      }
    },
    syncSelectedGroupWithFilters() {
      if (!this.filteredGroups.length) {
        this.selectedGroupId = null;
        this.selectedGroup = null;
        return;
      }
      if (!this.selectedGroupId || !this.filteredGroups.find((g) => g.grupo_precio_id === this.selectedGroupId)) {
        this.selectGroup(this.filteredGroups[0].grupo_precio_id);
      }
    },
    resetGroupForm() {
      this.groupForm = {
        grupo_precio_id: null,
        nombre: '',
        descripcion: '',
        unidad_medida_id: null
      };
    },
    editCurrentGroup() {
      if (!this.selectedGroup) return;
      this.groupForm = {
        grupo_precio_id: this.selectedGroup.grupo_precio_id,
        nombre: this.selectedGroup.nombre,
        descripcion: this.selectedGroup.descripcion || '',
        unidad_medida_id: this.selectedGroup.unidad_medida_id
      };
    },
    async saveGroup() {
      this.loadingSave = true;
      try {
        if (this.groupForm.grupo_precio_id) {
          await updatePriceGroup(this.groupForm.grupo_precio_id, {
            nombre: this.groupForm.nombre,
            descripcion: this.groupForm.descripcion
          });
          this.notify('Grupo actualizado correctamente');
        } else {
          const created = await createPriceGroup({
            nombre: this.groupForm.nombre,
            descripcion: this.groupForm.descripcion,
            unidad_medida_id: this.groupForm.unidad_medida_id
          });
          this.selectedGroupId = created.grupo_precio_id;
          this.notify('Grupo creado correctamente');
        }
        this.resetGroupForm();
        await this.fetchGroups();
        if (this.mobileViewport && this.selectedGroupId) {
          await this.selectGroup(this.selectedGroupId);
          this.mobileStep = 'detalle';
        }
      } catch (error) {
        this.notify(this.readError(error, 'No se pudo guardar el grupo'), 'error');
      } finally {
        this.loadingSave = false;
      }
    },
    async toggleGroupStatus(group) {
      try {
        if (group.activo) {
          await deactivatePriceGroup(group.grupo_precio_id);
          this.notify('Grupo inactivado');
        } else {
          await activatePriceGroup(group.grupo_precio_id);
          this.notify('Grupo activado');
        }
        await this.fetchGroups();
        if (this.mobileViewport && this.mobileStep === 'detalle' && this.selectedGroupId) {
          await this.selectGroup(this.selectedGroupId);
        }
      } catch (error) {
        this.notify(this.readError(error, 'No se pudo cambiar el estado del grupo'), 'error');
      }
    },
    async searchProducts() {
      if (!this.searchInput || this.searchInput.length < 2 || !this.selectedGroupId) {
        this.searchResults = [];
        return;
      }
      try {
        this.searchResults = await searchPriceGroupProducts({ q: this.searchInput, limit: 12 });
      } catch (error) {
        this.searchResults = [];
      }
    },
    async addByProductId(productId) {
      if (!this.selectedGroupId) return;
      try {
        await addProductToPriceGroup(this.selectedGroupId, { producto_id: Number(productId) });
        this.searchInput = '';
        this.searchResults = [];
        this.notify('Producto agregado al grupo');
        await this.selectGroup(this.selectedGroupId);
        await this.fetchGroups();
      } catch (error) {
        this.notify(this.readError(error, 'No se pudo agregar el producto'), 'error');
      }
    },
    async addByBarcode() {
      if (!this.selectedGroupId || !this.barcodeInput) return;
      try {
        await addProductToPriceGroup(this.selectedGroupId, { codigo_barras: this.barcodeInput });
        this.barcodeInput = '';
        this.notify('Producto agregado por código');
        await this.selectGroup(this.selectedGroupId);
        await this.fetchGroups();
      } catch (error) {
        this.notify(this.readError(error, 'No se pudo agregar por código'), 'error');
      }
    },
    async removeProduct(productId) {
      if (!this.selectedGroupId) return;
      try {
        await removeProductFromPriceGroup(this.selectedGroupId, Number(productId));
        this.notify('Producto quitado del grupo');
        await this.selectGroup(this.selectedGroupId);
        await this.fetchGroups();
      } catch (error) {
        this.notify(this.readError(error, 'No se pudo quitar el producto'), 'error');
      }
    },
    async applyPrice() {
      if (!this.selectedGroupId) return;
      try {
        // `await`: `window.confirm` es el diálogo de la app y devuelve una PROMESA;
        // sin esperarla, «Cancelar» no cancelaba (FIX_HALLAZGOS_DE_VERIFICACION, F-2).
        const confirmed = await window.confirm('Esta acción actualizará precios para todos los productos activos del grupo. ¿Deseas continuar?');
        if (!confirmed) return;

        const payload = {
          precio_venta: this.priceForm.precio_venta,
          cantidad_desde: this.priceForm.cantidad_desde || 1
        };
        if (this.priceForm.cantidad_hasta) payload.cantidad_hasta = this.priceForm.cantidad_hasta;
        if (this.priceForm.vigencia_desde) payload.vigencia_desde = this.priceForm.vigencia_desde;

        const result = await applyPriceToGroup(this.selectedGroupId, payload);
        this.applyResult = result;
        this.notify(`Precio aplicado: ${result?.resumen?.actualizados || 0} actualizados, ${result?.resumen?.omitidos || 0} omitidos`);
      } catch (error) {
        this.applyResult = null;
        this.notify(this.readError(error, 'No se pudo aplicar el precio del grupo'), 'error');
      }
    }
  }
};
</script>

<style scoped>
/* ── Estilos compartidos ── */
.page-eyebrow {
  margin: 0;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: .08em;
  color: #6c757d;
}
h2 { margin: 0; }

.state-pill { font-size: .75rem; padding: .2rem .45rem; border-radius: 999px; }
.state-pill.on { background: #d1e7dd; color: #0f5132; }
.state-pill.off { background: #f8d7da; color: #842029; }

.search-results {
  border: 1px solid #dee2e6; border-radius: .5rem; background: #fff; max-height: 220px; overflow: auto;
}
.search-item {
  display: flex; flex-direction: column; width: 100%; border: 0; border-bottom: 1px solid #f1f1f1;
  background: #fff; text-align: left; padding: .55rem .7rem;
}
.search-item:last-child { border-bottom: 0; }
.search-item small { color: #6c757d; }

/* ── Desktop ── */
.page-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}
.toolbar-actions { display: flex; align-items: center; gap: .75rem; }
.layout-grid { display: grid; grid-template-columns: 1fr 1.3fr; gap: 1rem; }
.panel { min-height: 420px; }
.group-list { display: grid; gap: .5rem; }
.group-item {
  width: 100%; border: 1px solid #dee2e6; border-radius: .5rem;
  background: #fff; display: flex; justify-content: space-between; align-items: center;
  padding: .75rem; text-align: left;
}
.group-item.active { border-color: #212529; background: #f8f9fa; }

/* ── Mobile ── */
.mobile-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: .75rem;
  margin-bottom: 1rem;
}

.mobile-group-cards { display: grid; gap: .5rem; }

.mobile-group-card {
  width: 100%;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: .5rem;
  padding: .9rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: left;
  min-height: 60px;
  transition: background .12s;
}
.mobile-group-card:active { background: #f8f9fa; }
.mobile-group-card-info { flex: 1; min-width: 0; }

.mobile-group-info {
  padding: .85rem 1rem;
  background: #f8f9fa;
  border-radius: .5rem;
}

.mobile-add-tabs {
  display: flex;
  border: 1px solid #dee2e6;
  border-radius: .5rem;
  overflow: hidden;
}
.mobile-tab {
  flex: 1;
  padding: .6rem .5rem;
  background: #fff;
  border: none;
  font-size: .9rem;
  color: #6c757d;
  transition: background .12s, color .12s;
}
.mobile-tab.active {
  background: #212529;
  color: #fff;
  font-weight: 600;
}

.mobile-form-footer {
  display: flex;
  gap: .75rem;
  justify-content: flex-end;
  padding-top: .75rem;
  border-top: 1px solid #dee2e6;
  margin-top: .5rem;
}

.product-card-list { display: flex; flex-direction: column; gap: .5rem; }
.product-card-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: .75rem;
  padding: .6rem .75rem;
  background: #f8f9fa;
  border-radius: .4rem;
}
.product-card-info { flex: 1; min-width: 0; }
</style>
